import { Chip, Image, RichText } from '@/features/shared';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { MuiButtonBase, MuiCard, MuiTypography } from '@/features/shared/material-ui';
import { colorMap, Theme } from '@/utils/constants';
import {
  mapColorNameToContrastColorName,
  mapLinkToProps,
  matchExternalLink,
} from '@/utils/helpers';
import clsx from 'clsx';
import { get, includes, isEmpty, keys, map } from 'lodash/fp';
import React, { memo, PropsWithChildren, useContext, useMemo } from 'react';
import { SearchInProviders } from '../ResultsBlock/ResultsBlock.helpers';
import useStyles from './Card.styles';

const CardResult = ({
  tags = {},
  imageUrl,
  cardImage,
  cardHeading,
  cardDescription,
  url,
  theme = Theme.light,
  labels,
  config,
  certified,
  cardColor = colorMap.white,
  resultID,
  resultLabels = {},
  engine = '',
}: // partner,
PropsWithChildren<any>) => {
  const chipColor = useMemo(
    () => mapColorNameToContrastColorName({ colorName: cardColor, darker: true }),
    [cardColor],
  );
  const classes = useStyles({});
  const linkProps = useMemo(
    () => mapLinkToProps({ href: url, openInNewWindow: url && matchExternalLink(url) }),
    [url],
  );
  const { queryParams, updateQueryParams, facets } = useContext(ElasticComponentContext);

  const cardChips = React.useMemo(() => {
    const availableChipTypes = map(get('name'), facets);
    return keys(tags).reduce((acc: any, tagKey: string) => {
      if (!includes(tagKey, availableChipTypes)) return { ...acc };
      return { ...acc, [tagKey]: tags[tagKey] };
    }, {});
  }, [tags, facets]);

  const cardLabels = useMemo(() => {
    return keys(resultLabels).reduce((acc, labelKey) => {
      return {
        [labelKey]: resultLabels[labelKey],
      };
    }, {});
  }, [resultLabels]);

  const isSearchInBoth = useMemo(() => {
    const regex = new RegExp(`${SearchInProviders.IN_BOTH}$`);
    return regex.test(engine);
  }, [engine]);

  const handleChipClick = ({ e, item }: { e: MouseEvent; item: any }) => {
    e.preventDefault();

    Object.keys(item).forEach((key) => {
      // In case filters weren't applied before then just apply it.
      if (!queryParams?.filters) {
        updateQueryParams(key, [item[key]], true);
        return;
      }

      const currentValue = queryParams.filters[key] || [];
      // If that tag was applied before do nothing.
      if (currentValue.find((tag: string) => tag === item[key])) return;

      // Previous tag values needs to be added to the same facet.
      updateQueryParams(key, [...currentValue, item[key]], true);
    });
  };
  const rootClasses = clsx(classes.root, `${theme}-theme`);

  return (
    <MuiButtonBase
      {...linkProps}
      className={classes.button}
      aria-label={cardHeading}
      data-resultid={resultID}
      data-datalayer-title={cardHeading}
    >
      <MuiCard className={rootClasses} elevation={3} data-testid="qa-card-component">
        <article>
          {(cardImage || imageUrl) && (
            <div className={classes.image} data-testid="qa-card-component-image">
              {cardImage && <Image image={cardImage} type="card" size="cover" position="center" />}
              {imageUrl && <img src={imageUrl} alt={cardHeading} />}
            </div>
          )}
          <div className={clsx(classes.content, !isEmpty(cardChips) ? 'has-categories' : '')}>
            {cardHeading && (
              <header className={classes.header}>
                {!isEmpty(cardChips) && (
                  <>
                    <div>
                      {keys(cardChips).map((key: any, id: number) => (
                        <Chip
                          label={cardChips[key]}
                          // eslint-disable-next-line react/no-array-index-key
                          key={id}
                          size="small"
                          clickable
                          className={classes.chip}
                          multiple={keys(cardChips).length > 1}
                          theme={theme}
                          chipColor={chipColor}
                          onClick={(e: MouseEvent) =>
                            handleChipClick({ e, item: { [key]: cardChips[key] } })
                          }
                        />
                      ))}
                    </div>
                  </>
                )}
                {!isEmpty(cardLabels) && isSearchInBoth && (
                  <div>
                    {keys(cardLabels).map((cardLabelKey) => (
                      <MuiTypography key={cardLabelKey} variant="overline">
                        {cardLabels[cardLabelKey]}
                      </MuiTypography>
                    ))}
                  </div>
                )}
                <MuiTypography variant="h5" className={classes.title}>
                  {cardHeading}
                </MuiTypography>
              </header>
            )}
            {cardDescription && (
              <main>
                <RichText className={classes.description} content={cardDescription} theme={theme} />
              </main>
            )}
            {certified && (
              <span
                className={classes.certified}
                aria-label={labels?.partner_certified || 'Partner (Certified)'}
              >
                {labels?.partner_certified || 'Partner (Certified)'}
              </span>
            )}
          </div>
        </article>
      </MuiCard>
    </MuiButtonBase>
  );
};

export default memo(CardResult);
